import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Check, X, Trash2, Download, Layout as LayoutIcon, ListChecks, FileSpreadsheet, ExternalLink, Printer } from "lucide-react";
import { toast } from "sonner";
import { photoUrl } from "@/lib/format";

export default function Registrations() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("list");
  const [filter, setFilter] = useState("pending");

  const load = async () => {
    const { data } = await api.get("/registrations");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try { await api.post(`/registrations/${id}/approve`); toast.success("Approved → Player created"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const reject = async (id) => {
    try { await api.post(`/registrations/${id}/reject`); toast("Rejected"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this registration?")) return;
    try { await api.delete(`/registrations/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const downloadCsv = async () => {
    const { data } = await api.get("/registrations-export.csv", { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "mara-registrations.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded");
  };
  const printPosters = () => { window.print(); };

  const verifyPayment = async (id, r) => {
    try {
      await api.post(`/registrations/${id}/verify-payment`, { payment_method: r.payment_method || "", payment_reference: r.payment_reference || "" });
      toast.success("Payment verified");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const refundFee = async (id) => {
    try { await api.post(`/registrations/${id}/refund-fee`); toast.success("Refund flagged"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const autoRefundUnsold = async () => {
    if (!window.confirm("Flag refunds for ALL paid, approved, unsold players?")) return;
    try {
      const r = await api.post("/registrations/auto-refund-unsold");
      toast.success(`Flagged ${r.data.refunded} refund(s)`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = items.filter((r) => filter === "all" || r.status === filter);
  const counts = {
    all: items.length,
    pending: items.filter((r) => r.status === "pending").length,
    approved: items.filter((r) => r.status === "approved").length,
    rejected: items.filter((r) => r.status === "rejected").length,
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl print:p-0 print:max-w-none">
      <style>{`@media print { .no-print { display: none !important; } body { background: white !important; color: black !important; } }`}</style>

      <div className="mb-6 flex items-start justify-between flex-wrap gap-4 no-print">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Module 2 · Player Pipeline</div>
          <h1 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter text-white">Registrations</h1>
          <div className="text-xs text-zinc-500 mt-1">{counts.all} total · {counts.pending} pending · {counts.approved} approved · {counts.rejected} rejected</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button data-testid="btn-export-csv" onClick={downloadCsv} variant="outline" className="border-white/20 text-white">
            <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Export Excel/CSV
          </Button>
          <Button data-testid="btn-print-posters" onClick={printPosters} variant="outline" className="border-white/20 text-white">
            <Printer className="w-4 h-4 mr-1.5" /> Print Posters
          </Button>
          <Button data-testid="btn-auto-refund" onClick={autoRefundUnsold} variant="outline" className="border-[#E32424]/40 text-[#E32424]">
            Auto-Refund Unsold
          </Button>
        </div>
      </div>

      {/* Filter */}
      <div className="mb-4 flex gap-2 flex-wrap no-print">
        {[
          { key: "pending", label: "Pending" },
          { key: "approved", label: "Approved" },
          { key: "rejected", label: "Rejected" },
          { key: "all", label: "All" },
        ].map((f) => (
          <button key={f.key} data-testid={`filter-${f.key}`} onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-md text-xs uppercase tracking-widest ${filter === f.key ? "bg-[#00A86B] text-white" : "bg-[#11241b] text-zinc-400 border border-white/10"}`}>
            {f.label} <span className="ml-1 opacity-70">{counts[f.key]}</span>
          </button>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full no-print">
        <TabsList className="bg-[#11241b] border border-white/10 mb-4">
          <TabsTrigger data-testid="tab-list" value="list" className="data-[state=active]:bg-[#00A86B] data-[state=active]:text-white">
            <ListChecks className="w-4 h-4 mr-1.5" /> List View
          </TabsTrigger>
          <TabsTrigger data-testid="tab-posters" value="posters" className="data-[state=active]:bg-[#00A86B] data-[state=active]:text-white">
            <LayoutIcon className="w-4 h-4 mr-1.5" /> Poster View
          </TabsTrigger>
        </TabsList>

        <TabsContent value="list">
          <Card className="bg-[#11241b] border-white/10 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-zinc-500 text-left bg-[#060d09]">
                <tr><th className="p-3">Photo</th><th>Name</th><th>Email</th><th>Phone</th><th>Cat / Rating</th><th>Fee</th><th>Status</th><th></th></tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-t border-white/5" data-testid={`reg-row-${r.id}`}>
                    <td className="p-3">
                      <div className="w-12 h-12 rounded-md overflow-hidden bg-[#060d09]">
                        {r.photo_path ? <img src={photoUrl(r.photo_path)} className="w-full h-full object-cover" /> : null}
                      </div>
                    </td>
                    <td className="text-white">{r.name}<div className="text-[10px] text-zinc-500">{r.city || ""}</div></td>
                    <td className="text-zinc-400">{r.email}</td>
                    <td className="text-zinc-400">{r.phone}</td>
                    <td><span className="px-2 py-0.5 rounded bg-[#060d09] border border-white/10 text-[10px]">Cat {r.category}</span> <span className="text-white ml-1">{r.rating}★</span> {r.is_international && <span className="ml-1 text-amber-400 text-[10px] uppercase">Intl</span>}</td>
                    <td>
                      <div className="text-xs">
                        <div className="text-white">KES {(r.fee_amount || 5000).toLocaleString()}</div>
                        <div className="flex gap-1 mt-0.5">
                          {r.fee_verified ? <span className="px-1.5 py-0.5 rounded bg-[#00A86B]/15 text-[#00A86B] text-[9px] uppercase">✓ Verified</span> :
                            r.fee_paid ? <span className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 text-[9px] uppercase">Pending</span> :
                            <span className="px-1.5 py-0.5 rounded bg-[#BB0000]/15 text-[#E32424] text-[9px] uppercase">Unpaid</span>}
                          {r.fee_refunded && <span className="px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400 text-[9px] uppercase">Refunded</span>}
                        </div>
                        {r.payment_reference && <div className="text-[10px] text-zinc-500 mt-0.5 font-mono-score">{r.payment_reference}</div>}
                      </div>
                    </td>
                    <td>
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${
                        r.status === "approved" ? "bg-[#00A86B] text-white" :
                        r.status === "rejected" ? "bg-[#BB0000] text-white" :
                        "bg-zinc-800 text-zinc-300"
                      }`}>{r.status}</span>
                    </td>
                    <td className="text-right pr-3">
                      <div className="flex gap-1 justify-end flex-wrap">
                        {!r.fee_verified && (
                          <Button data-testid={`btn-verify-fee-${r.id}`} size="sm" onClick={() => verifyPayment(r.id, r)} className="bg-[#00A86B] hover:bg-[#00935A] text-white text-[10px]">Verify Payment</Button>
                        )}
                        {r.fee_paid && !r.fee_refunded && (
                          <Button data-testid={`btn-refund-fee-${r.id}`} size="sm" variant="outline" onClick={() => refundFee(r.id)} className="border-blue-500/40 text-blue-400 text-[10px]">Refund</Button>
                        )}
                        {r.status === "pending" && (
                          <>
                            <Button data-testid={`btn-approve-${r.id}`} size="sm" onClick={() => approve(r.id)} className="bg-emerald-500 hover:bg-emerald-400 text-black"><Check className="w-3.5 h-3.5" /></Button>
                            <Button data-testid={`btn-reject-${r.id}`} size="sm" variant="outline" onClick={() => reject(r.id)} className="border-red-500/40 text-red-400"><X className="w-3.5 h-3.5" /></Button>
                          </>
                        )}
                        {user.role === "super_admin" && (
                          <Button data-testid={`btn-delete-${r.id}`} size="sm" variant="outline" onClick={() => del(r.id)} className="border-white/10 text-zinc-400"><Trash2 className="w-3.5 h-3.5" /></Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan={8} className="text-center text-zinc-500 p-8">No registrations.</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="posters">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 print:gap-2">
            {filtered.map((r) => <Poster key={r.id} r={r} />)}
            {filtered.length === 0 && <div className="col-span-full text-center text-zinc-500 py-8">No registrations.</div>}
          </div>
        </TabsContent>
      </Tabs>

      {/* Print-only poster view */}
      <div className="hidden print:block">
        <h1 className="font-anton text-4xl uppercase mb-4">Mara Padel League — Player Registrations</h1>
        <div className="grid grid-cols-2 gap-3">
          {filtered.map((r) => <Poster key={r.id} r={r} print />)}
        </div>
      </div>
    </div>
  );
}

function Poster({ r, print }) {
  return (
    <Card className={`overflow-hidden ${print ? "bg-white text-black border-zinc-300" : "bg-[#11241b] border-white/10"} break-inside-avoid`} data-testid={`poster-${r.id}`}>
      <div className="grid grid-cols-[120px_1fr] gap-3">
        <div className={`aspect-[3/4] ${print ? "bg-zinc-100" : "bg-[#060d09]"}`}>
          {r.photo_path ? <img src={photoUrl(r.photo_path)} className="w-full h-full object-cover" /> : null}
        </div>
        <div className="p-3 pr-4">
          <div className={`text-[9px] uppercase tracking-widest ${print ? "text-zinc-500" : "text-zinc-500"}`}>Cat {r.category} · {r.country}</div>
          <div className={`font-anton text-2xl uppercase tracking-tight leading-none mt-0.5 ${print ? "text-black" : "text-white"}`}>{r.name}</div>
          <div className={`text-xs mt-2 space-y-0.5 ${print ? "text-zinc-700" : "text-zinc-300"}`}>
            <div>★ {r.rating} · Seed {r.seed_level}</div>
            {r.is_international && <div className={print ? "text-amber-700 font-bold" : "text-amber-400"}>International</div>}
            <div>📞 {r.phone}</div>
            <div className="truncate">✉ {r.email}</div>
            {r.city && <div>📍 {r.city}</div>}
            {r.instagram && <div>IG {r.instagram}</div>}
          </div>
          {r.achievements && (
            <div className={`text-[10px] mt-2 ${print ? "text-zinc-600" : "text-zinc-400"} line-clamp-3`}>
              <span className="uppercase tracking-widest">Achievements</span>
              <div className="mt-0.5">{r.achievements}</div>
            </div>
          )}
          {(r.video_url_1 || r.video_url_2 || r.video_url_3) && !print && (
            <div className="mt-2 flex gap-1 flex-wrap">
              {[r.video_url_1, r.video_url_2, r.video_url_3].filter(Boolean).map((u, i) => (
                <a key={i} href={u} target="_blank" rel="noreferrer" className="text-[10px] px-2 py-0.5 bg-[#00A86B]/10 border border-[#00A86B]/30 text-[#00A86B] rounded inline-flex items-center gap-1">
                  <ExternalLink className="w-2.5 h-2.5" /> Video {i + 1}
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
